import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Background } from '../../shared/components/background/background';
import { Connect } from '../../shared/components/connect/connect';
import { HeroSection } from '../../shared/components/hero-section/hero-section';
import { Info } from '../../shared/components/info/info';
import { Button } from '../../shared/components/button/button';
import { ContentSection } from '../../shared/components/content-section/content-section';
import { LeadSection } from '../landing/components/lead-section/lead-section';
import { TagServices } from '../landing/components/tag-services/tag-services';
import { IconSection } from '../landing/components/icon-section/icon-section';
import { StepsSection } from '../landing/components/steps-section/steps-section';
import { RevealDirective } from '../../shared/directives/reveal.directive';
import { SpotlightDirective } from '../../shared/directives/spotlight.directive';
import { CalendlyService } from '../../shared/services/calendly.service';
import { SeoService } from '../../shared/services/seo.service';
import {
  DEV_SECTIONS_INTRO,
  DEV_SECTIONS_PLATFORM,
  DEV_SECTIONS_PROCESS,
} from '../../shared/data/app-development-sections';

const FAQS: { q: string; a: string }[] = [
  {
    q: 'What does your mobile app development company build?',
    a: 'SEMNexus builds custom iOS, Android, cross-platform, and AI-powered applications — from startup MVPs to enterprise platforms. We handle the full lifecycle: discovery and planning, UI/UX design, development, quality assurance, deployment, and ongoing maintenance.',
  },
  {
    q: 'Do you develop native iOS and Android apps or cross-platform apps?',
    a: 'Both. We build native iOS apps for iPhone, iPad, and Apple Watch, native Android apps optimized for the diverse Android ecosystem, and cross-platform apps with React Native and similar frameworks when a shared codebase lowers cost and speeds up launch.',
  },
  {
    q: 'Can you build AI-powered applications?',
    a: 'Yes. Our AI app development services cover machine learning, large language models, automation, predictive analytics, recommendation engines, and generative AI products — designed around your business objectives rather than bolted on afterward.',
  },
  {
    q: 'What does your app development process look like?',
    a: 'We follow an agile, sprint-based process with six stages: Discovery, Design, Development, Quality Assurance, Deployment, and Maintenance. Clients review progress and provide feedback throughout, which keeps the build transparent and flexible.',
  },
  {
    q: 'Do you help with design and prototyping before development starts?',
    a: 'Yes. Before any code is written, our design team produces wireframes, user flows, interactive prototypes, and high-fidelity designs so stakeholders can validate concepts early — reducing risk and ensuring the product aligns with user expectations and business goals.',
  },
];

@Component({
  selector: 'app-app-development-company',
  imports: [
    Background,
    HeroSection,
    ContentSection,
    LeadSection,
    TagServices,
    IconSection,
    StepsSection,
    RevealDirective,
    SpotlightDirective,
    Info,
    Button,
    Connect,
    RouterLink,
  ],
  templateUrl: './app-development-company.html',
  styleUrl: './app-development-company.scss',
})
export class AppDevelopmentCompany implements OnInit {
  protected file = 'app-development';
  /** Hero heading — "app" in the brand gradient, "development" in white. */
  protected heroTitleHtml = '<span class="gradient-text">app</span><br>development<br><span class="gradient-text">services</span>';

  // Band 1 intro: section 0 ("mobile app development company") renders as a split header
  // (lead-section), section 1 ("custom app development services") as split header + check-pill
  // tags (tag-services); the rest (iOS) keep the generic centered content-section.
  protected introA = DEV_SECTIONS_INTRO[0];
  protected introB = DEV_SECTIONS_INTRO[1];
  protected iosSection = DEV_SECTIONS_INTRO[2];
  // Band 2: Android + AI render as icon sections; cross-platform as a glass-card split header.
  protected androidSection = DEV_SECTIONS_PLATFORM[0];
  protected crossPlatformSection = DEV_SECTIONS_PLATFORM[1];
  protected aiSection = DEV_SECTIONS_PLATFORM[2];
  // Band 3: "ui/ux design & app prototyping" renders centered with check-pill tags;
  // the rest (agile process, why-choose) keep the generic centered content-section.
  protected uiuxSection = DEV_SECTIONS_PROCESS[0];
  protected agileSection = DEV_SECTIONS_PROCESS[1];
  protected whyChooseSection = DEV_SECTIONS_PROCESS[2];

  private router = inject(Router);
  private calendly = inject(CalendlyService);
  private seo = inject(SeoService);

  ngOnInit() {
    this.seo.apply({
      title: 'Mobile App Development Company',
      description: 'SEMNexus is a mobile app development company building custom iOS, Android, cross-platform, and AI-powered applications — from startup MVPs to enterprise platforms — with UX/UI design, agile delivery, and full-lifecycle support.',
      canonical: '/app-development-company/',
      image: 'https://semnexus.com/images/app-development/backgrounds/app-development.avif',
      imageAlt: 'App development services by SEM Nexus',
      type: 'website',
    });
    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: 'App Development' },
    ]);
    this.seo.setJsonLd('service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://semnexus.com/app-development-company/#service',
      name: 'Mobile App Development',
      serviceType: 'iOS, Android, cross-platform, and AI app development',
      description: 'Custom iOS, Android, cross-platform, and AI-powered mobile and software development with UI/UX design, agile delivery, QA, and full product-lifecycle support.',
      provider: { '@id': 'https://semnexus.com/#organization' },
      areaServed: { '@type': 'Country', name: 'United States' },
      url: 'https://semnexus.com/app-development-company/',
      offers: {
        '@type': 'OfferCatalog',
        name: 'App development services',
        itemListElement: [
          { '@type': 'Offer', name: 'Custom App Development', description: 'Custom mobile, SaaS, web, and enterprise software built around each client’s workflows, audience, and growth plans, with API integrations, backend, and cloud infrastructure.' },
          { '@type': 'Offer', name: 'iOS App Development', description: 'High-performance native apps for iPhone, iPad, and Apple Watch built to Apple’s design, performance, and security standards.' },
          { '@type': 'Offer', name: 'Android App Development', description: 'Secure, scalable Android applications optimized to perform consistently across the diverse Android device ecosystem.' },
          { '@type': 'Offer', name: 'Cross-Platform App Development', description: 'Shared-codebase apps for iOS and Android using React Native and modern frameworks for near-native performance at lower cost.' },
          { '@type': 'Offer', name: 'AI App Development', description: 'AI-powered products using machine learning, LLMs, automation, predictive analytics, and recommendation engines.' },
          { '@type': 'Offer', name: 'UI/UX Design & Prototyping', description: 'Wireframing, interactive prototyping, UX/UI design, user testing, and design systems to validate concepts before build.' },
        ],
      },
    });
    this.seo.setJsonLd('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://semnexus.com/app-development-company/#faq',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  goToCaseStudies() {
    this.router.navigate(['development-case-studies']);
  }

  goToConsulting() {
    this.router.navigate(['business-mobile-consulting']);
  }

  scheduleCall() {
    this.calendly.openPopup().catch(() => {});
  }
}
